import { type DebouncedFunc, throttle } from 'lodash-es';

import type {
  UploaderState,
  UploaderStateStorageInterface,
  UploaderStateUpdater,
} from '@iconik-io/uploader/src/state';

import { PROGRESS_THROTTLE_MS } from './constants';
import { isProgressOnlyUpdate } from './is_progress_only_update';
import {
  type UploaderStatesAppState,
  uploaderStateDeleted,
  uploaderStateSet,
} from './states_slice';

export type UploaderStatesStore = {
  dispatch: (
    action: ReturnType<typeof uploaderStateSet | typeof uploaderStateDeleted>,
  ) => unknown;
  getState: () => UploaderStatesAppState;
};

/** Progress of an upload that the store has not been given yet. */
type PendingProgress = {
  state?: UploaderState;
  dispatch: DebouncedFunc<() => void>;
};

/**
 * Keeps the scheduler's upload states in the app's Redux store. Chunks report
 * progress many times a second, and every dispatch wakes every subscriber, so a
 * change that only moves the byte counts reaches the store at most once per
 * PROGRESS_THROTTLE_MS. Anything else is dispatched at once, taking any held
 * progress with it. Reads see held progress, so the scheduler never works from
 * a stale state; only the UI sees progress late.
 */
export class UploaderStateStorageRedux
  implements UploaderStateStorageInterface
{
  #store: UploaderStatesStore;

  #pendingProgress = new Map<string, PendingProgress>();

  constructor(store: UploaderStatesStore) {
    this.#store = store;
  }

  #getStoredState(stateId: string): UploaderState | undefined {
    return this.#store.getState().uploaderStates[stateId];
  }

  #read(stateId: string): UploaderState | undefined {
    return (
      this.#pendingProgress.get(stateId)?.state ?? this.#getStoredState(stateId)
    );
  }

  #dispatchPendingProgress(stateId: string) {
    const pending = this.#pendingProgress.get(stateId);
    if (!pending?.state) {
      return;
    }
    const state = pending.state;
    pending.state = undefined;
    this.#store.dispatch(uploaderStateSet({ stateId, state }));
  }

  #write(stateId: string, state: UploaderState) {
    const storedState = this.#getStoredState(stateId);
    if (storedState && isProgressOnlyUpdate(storedState, state)) {
      let pending = this.#pendingProgress.get(stateId);
      if (!pending) {
        pending = {
          dispatch: throttle(
            () => this.#dispatchPendingProgress(stateId),
            PROGRESS_THROTTLE_MS,
          ),
        };
        this.#pendingProgress.set(stateId, pending);
      }
      pending.state = state;
      pending.dispatch();
      return;
    }
    this.#dropPendingProgress(stateId);
    this.#store.dispatch(uploaderStateSet({ stateId, state }));
  }

  #dropPendingProgress(stateId: string) {
    const pending = this.#pendingProgress.get(stateId);
    if (pending) {
      pending.dispatch.cancel();
      pending.state = undefined;
    }
  }

  get(stateId: string): Promise<UploaderState | undefined> {
    return Promise.resolve(this.#read(stateId));
  }

  getAll(): Promise<UploaderState[]> {
    return Promise.resolve(
      Object.values(this.#store.getState().uploaderStates).map(
        (state) => this.#read(state.id) ?? state,
      ),
    );
  }

  set(stateId: string, state: UploaderState): Promise<void> {
    this.#write(stateId, state);
    return Promise.resolve();
  }

  update(
    stateId: string,
    updater: UploaderStateUpdater,
  ): Promise<UploaderState | undefined> {
    const state = this.#read(stateId);
    if (!state) {
      return Promise.resolve(undefined);
    }
    const updatedState = updater(state);
    this.#write(stateId, updatedState);
    return Promise.resolve(updatedState);
  }

  delete(stateId: string): Promise<void> {
    this.#dropPendingProgress(stateId);
    this.#pendingProgress.delete(stateId);
    this.#store.dispatch(uploaderStateDeleted(stateId));
    return Promise.resolve();
  }
}
